/**
 * Administering the platform: who is here, what they may do, and what the
 * deployment still needs.
 *
 * The shapes the administration screen reads, and the small amount of
 * arithmetic it does over them. Two rules hold throughout and both exist
 * because the opposite is the easy mistake:
 *
 *   · **A level is not a permission.** A role's access level decides which
 *     screens a client offers and what the assistant will discuss. Every
 *     endpoint still checks its own permission key, so raising a level shows
 *     somebody a screen whose data the API then refuses. That is noisy and
 *     safe; the reverse — a level that granted access — would be quiet and
 *     open. The screen says so wherever a level is edited.
 *
 *   · **The checklist is computed, never ticked.** Each step's state comes
 *     from the platform's own database on every read, so a step cannot be
 *     marked done while it is not, and it un-ticks itself if somebody undoes
 *     the thing.
 */
import type { AccessLevel } from "../assistant/access";
import { t, tn } from "../i18n";

export interface SetupStep {
  key: string;
  title: string;
  done: boolean;
  /** unsafe or unusable, as distinct from merely unfinished */
  blocking: boolean;
  /** what is true right now */
  is: string;
  /** what to do about it */
  next: string;
}

export interface SetupState {
  steps: SetupStep[];
  done: number;
  total: number;
  blocking: string[];
  ready: boolean;
  note: string;
}

export interface Person {
  id: string;
  kind: "user" | "service" | "device";
  externalId?: string;
  displayName: string;
  email?: string;
  groups: string[];
  roles: string[];
  lastSeenAt?: string;
  disabledAt?: string;
}

export interface RoleRow {
  key: string;
  name: string;
  description?: string;
  permissions: string[];
  accessLevel: AccessLevel;
  /** true for the roles the platform ships, which a tenant may not edit */
  builtin: boolean;
}

export interface NewPerson {
  displayName: string;
  externalId: string;
  email?: string;
  kind?: "user" | "service" | "device";
  roles: string[];
  siteId?: string;
}

export interface AddedPerson {
  id: string;
  displayName: string;
  roles: string[];
  refused: { role: string; why: string }[];
  accessLevel: AccessLevel;
  note: string;
}

export interface AuthStatus {
  method?: string;
  principal?: string;
  providers: AuthProvider[];
  developmentHeaderEnabled: boolean;
}

export interface AuthProvider {
  /** Absent from a platform older than the one that can change a provider from here. */
  id?: string;
  name: string;
  issuer: string;
  enabled: boolean;
  cachedKeys: number;
  defaultRole?: string;
  /** Whether people can be invited by email through it: only a provider whose accounts the platform may create. */
  canInvite: boolean;
}

/** Somebody to invite by email: the provider creates their account and sends the set-password link. */
export interface Invitation {
  displayName: string;
  email: string;
  role: string;
}

export interface Invited {
  displayName: string;
  email: string;
  role: string;
  accessLevel: AccessLevel;
  /** False when the email already had a sign-in account, which is then bound rather than duplicated. */
  accountCreated: boolean;
  /** True when this person was already listed; the role is added to them. */
  alreadyListed: boolean;
  emailSent: boolean;
}

/** What may be changed about a provider from the screen. */
export interface ProviderChange {
  enabled?: boolean;
  defaultRole?: string | null;
}

/* ── the reading the screen does ─────────────────────────────────────── */

/** Where a deployment stands, in one line, with the blocking steps first. */
export function setupSummary(s: SetupState): string {
  if (s.blocking.length)
    return s.blocking.length === 1
      ? t(
          "{done} of {total} steps done, and {n} of them is blocking — the platform is not ready to carry anything real.",
          {
            done: s.done,
            total: s.total,
            n: s.blocking.length,
          },
        )
      : t(
          "{done} of {total} steps done, and {n} of them are blocking — the platform is not ready to carry anything real.",
          {
            done: s.done,
            total: s.total,
            n: s.blocking.length,
          },
        );
  if (s.done === s.total) return t("All {total} steps done.", { total: s.total });
  return t("{done} of {total} steps done. Nothing blocking; the rest is honest progress.", {
    done: s.done,
    total: s.total,
  });
}

/** Blocking first, then unfinished, then done — the order somebody works them in. */
export function orderedSteps(s: SetupState): SetupStep[] {
  const rank = (x: SetupStep) => (x.blocking ? 0 : x.done ? 2 : 1);
  return [...s.steps].sort((a, b) => rank(a) - rank(b));
}

/** The level each person ends up with: the highest of the roles they hold. */
export function levelOfPerson(p: Person, roles: RoleRow[]): AccessLevel {
  const order: AccessLevel[] = ["operations", "engineering", "executive", "master"];
  let best = 0;
  for (const key of p.roles) {
    const r = roles.find((x) => x.key === key);
    if (r) best = Math.max(best, order.indexOf(r.accessLevel));
  }
  return order[best];
}

/** How many people hold a role that can administer the platform. */
export const administrators = (people: Person[], roles: RoleRow[]) =>
  people.filter((p) => !p.disabledAt && levelOfPerson(p, roles) === "master").length;

/**
 * What is worth saying about the people list, before anybody reads it row by
 * row. Each of these has bitten a real deployment.
 */
export function peopleWarnings(people: Person[], roles: RoleRow[]): string[] {
  const out: string[] = [];
  const active = people.filter((p) => !p.disabledAt);
  const noRole = active.filter((p) => p.roles.length === 0);
  if (noRole.length)
    out.push(
      tn(
        noRole.length,
        "{n} active person holds no role at all. They can sign in and will see an empty platform, which reads as a broken one.",
        "{n} active people hold no role at all. They can sign in and will see an empty platform, which reads as a broken one.",
      ),
    );
  const admins = administrators(people, roles);
  if (admins === 0)
    out.push(
      t("Nobody holds a role that can administer this platform. Nobody can grant a role, including to themselves."),
    );
  else if (admins === 1)
    out.push(
      t("One person can administer this platform. That is one illness away from nobody being able to grant a role."),
    );
  const noSubject = active.filter((p) => p.kind === "user" && !p.externalId);
  if (noSubject.length)
    out.push(
      tn(
        noSubject.length,
        "{n} person has no identity-provider subject, so the provider has nothing to bind them to and they can never sign in through it.",
        "{n} people have no identity-provider subject, so the provider has nothing to bind them to and they can never sign in through it.",
      ),
    );
  return out;
}
